import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap, cx } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import type { Status } from '@atlaskit/avatar/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { Approver } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ApprovalProfileCard } from '@confluence/approvals/entry-points/ApprovalProfileCard.ts';

const MAX_AVATARS_SHOWN = 2;

const styles = cssMap({
	facepile: {
		display: 'flex',
		alignItems: 'center',
		isolation: 'isolate',
		pointerEvents: 'none',
	},
	avatarContainer: {
		display: 'inline-flex',
	},
	overlap: {
		marginInlineStart: token('space.negative.050'),
	},
	moreIndicator: {
		display: 'inline-flex',
		marginInlineStart: token('space.negative.050'),
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
		boxSizing: 'border-box',
		borderColor: token('elevation.surface'),
		borderRadius: token('radius.full'),
		borderStyle: 'solid',
		borderWidth: token('border.width.selected'),
		backgroundColor: token('color.background.neutral'),
		color: token('color.text'),
	},
});

const i18n = defineMessages({
	profileCardLabel: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.profile-card-label',
		defaultMessage: 'Open profile card for {name}',
		description: "Accessible label for an approver avatar that opens the approver's profile card.",
	},
});

type ProfileAvatarFacepileProps = {
	approvers: Approver[];
	onPointerClick?: () => void;
};

const getAvatarStatus = (approver: Approver): Status | undefined => {
	if (approver.status === 'REVIEWER_APPROVED') {
		return 'approved';
	}

	if (approver.status === 'REVIEWER_DENIED') {
		return 'declined';
	}

	return undefined;
};

export const ProfileAvatarFacepile = ({
	approvers,
	onPointerClick,
}: ProfileAvatarFacepileProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const hasOverflow = approvers.length > MAX_AVATARS_SHOWN;
	const visibleApprovers = approvers.slice(0, hasOverflow ? 1 : MAX_AVATARS_SHOWN);
	const hiddenCount = approvers.length - visibleApprovers.length;

	if (visibleApprovers.length === 0) {
		return null;
	}

	return (
		<div css={styles.facepile} data-testid="approval-profile-avatar-facepile">
			{visibleApprovers.map((approver, index) => (
				<Box
					as="span"
					xcss={cx(styles.avatarContainer, index > 0 && styles.overlap)}
					key={`${approver.principalType}:${approver.principalId}:${approver.decisionId}`}
				>
					<ApprovalProfileCard
						ariaLabel={formatMessage(i18n.profileCardLabel, { name: approver.name })}
						fullName={approver.name}
						onPointerClick={onPointerClick}
						position="bottom-end"
						principalId={approver.principalId}
						principalType={approver.principalType}
						trigger="hover"
					>
						<span aria-hidden="true">
							<Avatar
								appearance="circle"
								size="small"
								src={approver.avatarUrl}
								stackIndex={visibleApprovers.length - index}
								status={getAvatarStatus(approver)}
								testId={`approval-profile-avatar-${index}`}
							/>
						</span>
					</ApprovalProfileCard>
				</Box>
			))}
			{hiddenCount > 0 && (
				<span
					aria-hidden="true"
					css={styles.moreIndicator}
					data-testid="approval-profile-avatar-overflow"
				>
					<Text size="small">+{hiddenCount}</Text>
				</span>
			)}
		</div>
	);
};
